'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCentrifugo } from '@/contexts/CentrifugoContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useProfileData } from '@/queries/useProfileQueries';
import { queryKeys } from '@/lib/queryClient';
import { useCatalog } from '@/screens/AchievementsScreen/hooks/useCatalog';
import { pickTitle, useAchievementLang } from '@/screens/AchievementsScreen/utils/pickLocale';
import { useOpenAchievementSheet } from '@/stores/achievementSheetStore';
import { useOpenClaimSheet } from '@/stores/claimSheetStore';
import { AchievementUnlockToast } from '@/components/AchievementUnlockToast';
import { Toast, ToastTile, showCustomToast } from '@/components/Toast';
import { BandTickets } from '@/components/BandTickets';
import { IoCheckmarkCircle } from '@/components/icons';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import bandTranslations from '@/screens/BandScreen/BandScreen.translations';
import { QUEST_COPY, renderQuestTitle } from '@/screens/BandScreen/questCopy';
import { scrollToBandQuests } from '@/screens/BandScreen/bandQuestsAnchor';
import { loadSounds, playSfx } from '@/lib/audioEngine';
import { booleanField, numberField, rawField, stringField } from '@/lib/jsonBody';
import type { CatalogReward, CatalogResponse } from '@/services/generated/Api';

const UNLOCK_SFX = 'achievement_unlock';
const TOAST_DURATION_MS = 6000;

interface AchievementUnlockedMessage {
  type: 'achievement_unlocked';
  key?: string;
  unlockedAt?: string;
  reward?: CatalogReward;
  claimed?: boolean;
  hiddenUntilUnlocked?: boolean;
}

interface BandQuestCompletedMessage {
  type: 'band_quest_completed';
  questId?: string;
  tickets?: number;
  completedAt?: string;
}

const parseReward = (value: unknown): CatalogReward => ({
  xp: numberField(value, 'xp'),
  credits: numberField(value, 'credits'),
});

const parseAchievementUnlocked = (data: unknown): AchievementUnlockedMessage => ({
  type: 'achievement_unlocked',
  key: stringField(data, 'key'),
  unlockedAt: stringField(data, 'unlockedAt'),
  reward: parseReward(rawField(data, 'reward')),
  claimed: booleanField(data, 'claimed'),
  hiddenUntilUnlocked: booleanField(data, 'hiddenUntilUnlocked'),
});

const parseBandQuestCompleted = (data: unknown): BandQuestCompletedMessage => ({
  type: 'band_quest_completed',
  questId: stringField(data, 'questId'),
  tickets: numberField(data, 'tickets'),
  completedAt: stringField(data, 'completedAt'),
});

export const useUserNotificationsSubscription = () => {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();
  const profile = useProfileData();
  const userId = profile?.id;
  const openSheet = useOpenAchievementSheet();
  const openClaimSheet = useOpenClaimSheet();
  const lang = useAchievementLang();
  const { t: tBand } = useLocalTranslation(bandTranslations);
  useCatalog();

  const navigation = useNavigation();
  const navRef = useRef(navigation);
  navRef.current = navigation;

  useEffect(() => { void loadSounds([UNLOCK_SFX]).catch(() => undefined); }, []);

  const handleAchievementUnlocked = useCallback(
    async (event: AchievementUnlockedMessage, client: QueryClient, currentUserId: string) => {
      const key = String(event.key ?? '');

      if (event.hiddenUntilUnlocked) {
        await client.refetchQueries({ queryKey: queryKeys.achievementsCatalog, exact: true });
      }

      const catalog = client.getQueryData<CatalogResponse>(queryKeys.achievementsCatalog);
      const item = catalog?.items.find((i) => i.key === key);
      const title = pickTitle(item?.i18n, lang);
      const displayTitle = title && title !== '???' ? title : '🏆';
      const credits = event.reward?.credits ?? item?.reward.credits ?? 0;
      const xp = event.reward?.xp ?? item?.reward.xp ?? 0;
      const claimed = event.claimed ?? true;
      const iconFile = item?.iconFile ?? '';

      playSfx(UNLOCK_SFX, 0.6);

      showCustomToast(
        (id) => (
          <AchievementUnlockToast
            iconFile={iconFile}
            title={displayTitle}
            rewardCredits={credits}
            rewardXp={xp}
            claimed={claimed}
            onDismiss={() => toast.dismiss(id)}
            onClick={() => {
              if (navRef.current.getCurrentScreen().screen === 'case-roulette') return;
              if (!claimed) {
                navRef.current.navigate('achievements');
                openClaimSheet();
                return;
              }
              navRef.current.changeTab('profile');
              openSheet(key);
            }}
          />
        ),
        { duration: TOAST_DURATION_MS },
      );

      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.achievementsMine }),
        client.invalidateQueries({ queryKey: queryKeys.achievementsCatalog }),
        client.invalidateQueries({ queryKey: queryKeys.achievementsStats(key) }),
        client.invalidateQueries({ queryKey: queryKeys.achievementsRecentReceivers(key) }),
        client.invalidateQueries({ queryKey: queryKeys.achievementsUser(currentUserId) }),
        client.invalidateQueries({ queryKey: queryKeys.profile }),
        client.invalidateQueries({ queryKey: queryKeys.xpStatus }),
      ]);
    },
    [lang, openClaimSheet, openSheet],
  );

  const handleBandQuestCompleted = useCallback(
    async (event: BandQuestCompletedMessage, client: QueryClient) => {
      const tickets = event.tickets ?? 0;
      const title = renderQuestTitle(event.questId ? QUEST_COPY[event.questId] : undefined, tBand);

      playSfx(UNLOCK_SFX, 0.6);

      showCustomToast(
        (id) => (
          <Toast
            accent="blue"
            leading={
              <ToastTile>
                <span className="flex h-full w-full items-center justify-center text-accent-blue">
                  <IoCheckmarkCircle size={28} />
                </span>
              </ToastTile>
            }
            overline={tBand('questCompletedToastOverline')}
            title={title}
            description={
              <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-accent-blue">
                +<BandTickets amount={tickets} iconSize={11} />
              </span>
            }
            onDismiss={() => toast.dismiss(id)}
            onClick={() => {
              if (navRef.current.getCurrentScreen().screen === 'case-roulette') return;
              navRef.current.navigate('band');
              scrollToBandQuests();
            }}
          />
        ),
        { duration: TOAST_DURATION_MS },
      );

      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.bandQuests }),
        client.invalidateQueries({ queryKey: queryKeys.bandStatus }),
      ]);
    },
    [tBand],
  );

  useEffect(() => {
    if (!isConnected || !userId) return;

    const channel = channelPrefix ? `${channelPrefix}_user#${userId}` : `user#${userId}`;

    const unsubscribe = subscribe(channel, (data) => {
      void (async () => {
        switch (stringField(data, 'type')) {
          case 'achievement_unlocked':
            await handleAchievementUnlocked(parseAchievementUnlocked(data), queryClient, userId);
            break;
          case 'band_quest_completed':
            await handleBandQuestCompleted(parseBandQuestCompleted(data), queryClient);
            break;
          default:
            break;
        }
      })();
    });

    return unsubscribe;
  }, [
    isConnected,
    channelPrefix,
    userId,
    subscribe,
    queryClient,
    handleAchievementUnlocked,
    handleBandQuestCompleted,
  ]);
};
